/**
 * PulseIndicator — Generic composite score renderer with face visualization.
 *
 * This component knows NOTHING about what it's scoring. It receives a
 * pre-computed PulseResult from the backend and renders it. The backend
 * reads config (metrics, weights, thresholds, visual levels) and
 * computes everything. This file is a pure SVG + layout renderer.
 *
 * Backend responsibilities:
 *   - Read metric definitions from config
 *   - Query relevant data (tasks, actors, instances — whatever the config says)
 *   - Compute each metric score using config-defined formulas
 *   - Compute weighted composite score
 *   - Resolve visual level (face, color, label) from config thresholds
 *   - Return the complete PulseResult
 *
 * Frontend responsibilities (this file):
 *   - Render the face SVG using the color + face type from the result
 *   - Render the score number
 *   - Render breakdown bars if the result includes them
 *   - That's it. Zero domain logic.
 *
 * DRR-055: Dumb Frontend — UI only displays what backend defines.
 */

import {
  Paper,
  Text,
  Group,
  Stack,
  Progress,
  Tooltip,
  Loader,
} from '@mantine/core';

// ============================================================================
// TYPES — what the backend returns (matches GraphQL schema)
// ============================================================================

/** A single computed metric in the pulse result */
export interface PulseMetricResult {
  /** Opaque ID — frontend doesn't interpret this */
  id: string;
  /** Display name from config */
  name: string;
  /** Computed score 0-100 */
  score: number;
  /** Weight from config (for display only, backend already used it) */
  weight: number;
}

/** The resolved visual level from config thresholds */
export interface PulseVisualLevel {
  label: string;
  color: string;
  /** Face type key from config (maps to SVG geometry) */
  face: string;
  description: string;
}

/** Display options from config */
export interface PulseDisplayOptions {
  faceSizePx: number;
  showScore: boolean;
  showLabel: boolean;
  showBreakdown: boolean;
}

/** Complete pre-computed pulse result from backend */
export interface PulseResult {
  /** Display name from config (e.g. "Facility Pulse", "Team Health") */
  displayName: string;
  /** Composite score 0-100 */
  compositeScore: number;
  /** Resolved visual level */
  level: PulseVisualLevel;
  /** Per-metric breakdown */
  metrics: PulseMetricResult[];
  /** Display options for requested size */
  display: PulseDisplayOptions;
  /** Color thresholds from config for progress bars */
  progressColors: {
    good: string;
    warning: string;
    critical: string;
    goodThreshold: number;
    warningThreshold: number;
  };
}

interface PulseIndicatorProps {
  readonly result: PulseResult | null;
  readonly loading?: boolean;
}

// ============================================================================
// FACE SVG — purely geometric, driven by face type + color from result
// ============================================================================

/** Default geometry when face type is unrecognized */
const NEUTRAL_FACE = { mouthCurve: 0, eyeStretch: 1 } as const;

/** Maps face type string from config to SVG curve parameters */
const FACE_GEOMETRY: Record<string, { mouthCurve: number; eyeStretch: number }> = {
  smile: { mouthCurve: 8, eyeStretch: 0.6 },
  slight_smile: { mouthCurve: 4, eyeStretch: 0.8 },
  neutral: NEUTRAL_FACE,
  slight_frown: { mouthCurve: -4, eyeStretch: 1.2 },
  frown: { mouthCurve: -8, eyeStretch: 1.4 },
};

function PulseFace({ faceType, color, pixelSize }: Readonly<{ faceType: string; color: string; pixelSize: number }>) {
  const geo = FACE_GEOMETRY[faceType] ?? NEUTRAL_FACE;
  const cx = pixelSize / 2;
  const cy = pixelSize / 2;
  const r = pixelSize * 0.42;
  const eyeY = cy - r * 0.15;
  const eyeSpacing = r * 0.35;
  const mouthY = cy + r * 0.25;
  const eyeR = r * 0.08;
  const mouthWidth = r * 0.5;
  const curve = geo.mouthCurve * (pixelSize / 100);

  return (
    <svg width={pixelSize} height={pixelSize} viewBox={`0 0 ${pixelSize} ${pixelSize}`}>
      <circle cx={cx} cy={cy} r={r} fill={color} opacity={0.15} stroke={color} strokeWidth={pixelSize * 0.03} />
      <ellipse cx={cx - eyeSpacing} cy={eyeY} rx={eyeR} ry={eyeR * geo.eyeStretch} fill={color} />
      <ellipse cx={cx + eyeSpacing} cy={eyeY} rx={eyeR} ry={eyeR * geo.eyeStretch} fill={color} />
      <path
        d={`M ${cx - mouthWidth} ${mouthY} Q ${cx} ${mouthY + curve} ${cx + mouthWidth} ${mouthY}`}
        fill="none" stroke={color} strokeWidth={pixelSize * 0.025} strokeLinecap="round"
      />
    </svg>
  );
}

// ============================================================================
// COMPONENT — pure renderer, zero domain logic
// ============================================================================

export function PulseIndicator({ result, loading }: PulseIndicatorProps) {
  if (loading) {
    return (
      <Group gap="xs">
        <Loader size="sm" />
      </Group>
    );
  }

  if (!result) return null;

  const { displayName, compositeScore, level, metrics, display, progressColors } = result;

  const getBarColor = (score: number): string => {
    if (score >= progressColors.goodThreshold) return progressColors.good;
    if (score >= progressColors.warningThreshold) return progressColors.warning;
    return progressColors.critical;
  };

  // Compact display (header)
  if (!display.showBreakdown) {
    return (
      <Tooltip label={`${displayName}: ${compositeScore} — ${level.label}`}>
        <Group gap={6} style={{ cursor: 'default' }}>
          <PulseFace faceType={level.face} color={level.color} pixelSize={display.faceSizePx} />
          <div>
            {display.showScore && (
              <Text size="xs" fw={700} style={{ color: level.color }}>{compositeScore}</Text>
            )}
            {display.showLabel && (
              <Text size="xs" c="dimmed">{level.label}</Text>
            )}
          </div>
        </Group>
      </Tooltip>
    );
  }

  // Full display (dashboard panel)
  return (
    <Paper p="lg" withBorder radius="md">
      <Group align="flex-start" gap="xl">
        <Stack align="center" gap="xs">
          <PulseFace faceType={level.face} color={level.color} pixelSize={display.faceSizePx} />
          {display.showScore && (
            <Text size="xl" fw={700} style={{ color: level.color }}>{compositeScore}</Text>
          )}
          {display.showLabel && (
            <Text size="sm" fw={500}>{level.label}</Text>
          )}
          <Text size="xs" c="dimmed">{level.description}</Text>
        </Stack>

        <Stack gap="sm" style={{ flex: 1 }}>
          {metrics.map((m) => (
            <div key={m.id}>
              <Group justify="space-between" mb={2}>
                <Text size="xs">{m.name} ({Math.round(m.weight * 100)}%)</Text>
                <Text size="xs" fw={500} style={{ color: getBarColor(m.score) }}>
                  {m.score}
                </Text>
              </Group>
              <Progress value={m.score} color={getBarColor(m.score)} size="sm" radius="xl" />
            </div>
          ))}
        </Stack>
      </Group>
    </Paper>
  );
}

export default PulseIndicator;
